import { __extends } from "tslib";
import { Texture } from "../../Materials/Textures/texture.js";
import { RenderTargetTexture } from "../../Materials/Textures/renderTargetTexture.js";

import "../../Engines/Extensions/engine.multiRender.js";
/**
 * A multi render target, like a render target provides the ability to render to a texture.
 * Unlike the render target, it can render to several draw buffers in one draw.
 * This is specially interesting in deferred rendering or for any effects requiring more than
 * just one color from a single pass.
 */
var MultiRenderTarget = /** @class */ (function (_super) {
    __extends(MultiRenderTarget, _super);
    /**
     * Instantiate a new multi render target texture.
     * A multi render target, like a render target provides the ability to render to a texture.
     * Unlike the render target, it can render to several draw buffers in one draw.
     * This is specially interesting in deferred rendering or for any effects requiring more than
     * just one color from a single pass.
     * @param name Define the name of the texture
     * @param size Define the size of the buffers to render to
     * @param count Define the number of target we are rendering into
     * @param scene Define the scene the texture belongs to
     * @param options Define the options used to create the multi render target
     * @param textureNames Define the names to set to the textures (if count > 0 - optional)
     */
    function MultiRenderTarget(name, size, count, scene, options, textureNames) {
        var _this = this;
        var generateMipMaps = options && options.generateMipMaps ? options.generateMipMaps : false;
        var generateDepthTexture = options && options.generateDepthTexture ? options.generateDepthTexture : false;
        var depthTextureFormat = options && options.depthTextureFormat ? options.depthTextureFormat : 15;
        var doNotChangeAspectRatio = !options || options.doNotChangeAspectRatio === undefined ? true : options.doNotChangeAspectRatio;
        var drawOnlyOnFirstAttachmentByDefault = options && options.drawOnlyOnFirstAttachmentByDefault ? options.drawOnlyOnFirstAttachmentByDefault : false;
        _this = _super.call(this, name, size, scene, generateMipMaps, doNotChangeAspectRatio, undefined, undefined, undefined, undefined, undefined, undefined, undefined, true) || this;
        if (!_this.isSupported) {
            _this.dispose();
            return _this;
        }
        var types = [];
        var samplingModes = [];
        var useSRGBBuffers = [];
        _this._initTypes(count, types, samplingModes, useSRGBBuffers, options);
        var generateDepthBuffer = !options || options.generateDepthBuffer === undefined ? true : options.generateDepthBuffer;
        var generateStencilBuffer = !options || options.generateStencilBuffer === undefined ? false : options.generateStencilBuffer;
        _this._size = size;
        _this._multiRenderTargetOptions = {
            samplingModes: samplingModes,
            generateMipMaps: generateMipMaps,
            generateDepthBuffer: generateDepthBuffer,
            generateStencilBuffer: generateStencilBuffer,
            generateDepthTexture: generateDepthTexture,
            depthTextureFormat: depthTextureFormat,
            types: types,
            textureCount: count,
            useSRGBBuffers: useSRGBBuffers,
        };
        _this._count = count;
        _this._drawOnlyOnFirstAttachmentByDefault = drawOnlyOnFirstAttachmentByDefault;
        if (count > 0) {
            _this._createInternalTextures();
            _this._createTextures(textureNames);
        }
        return _this;
    }
    Object.defineProperty(MultiRenderTarget.prototype, "isSupported", {
        /**
         * Get if draw buffers are currently supported by the used hardware and browser.
         */
        get: function () {
            var _a, _b;
            return (_b = (_a = this._engine) === null || _a === void 0 ? void 0 : _a.getCaps().drawBuffersExtension) !== null && _b !== void 0 ? _b : false;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(MultiRenderTarget.prototype, "textures", {
        /**
         * Get the list of textures generated by the multi render target.
         */
        get: function () {
            return this._textures;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(MultiRenderTarget.prototype, "count", {
        /**
         * Gets the number of textures in this MRT. This number can be different from `_textures.length` in case a depth texture is generated.
         */
        get: function () {
            return this._count;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(MultiRenderTarget.prototype, "depthTexture", {
        /**
         * Get the depth texture generated by the multi render target if options.generateDepthTexture has been set
         */
        get: function () {
            return this._textures[this._textures.length - 1];
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(MultiRenderTarget.prototype, "wrapU", {
        /**
         * Set the wrapping mode on U of all the textures we are rendering to.
         * Can be any of the Texture. (CLAMP_ADDRESSMODE, MIRROR_ADDRESSMODE or WRAP_ADDRESSMODE)
         */
        set: function (wrap) {
            if (this._textures) {
                for (var i = 0; i < this._textures.length; i++) {
                    this._textures[i].wrapU = wrap;
                }
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(MultiRenderTarget.prototype, "wrapV", {
        /**
         * Set the wrapping mode on V of all the textures we are rendering to.
         * Can be any of the Texture. (CLAMP_ADDRESSMODE, MIRROR_ADDRESSMODE or WRAP_ADDRESSMODE)
         */
        set: function (wrap) {
            if (this._textures) {
                for (var i = 0; i < this._textures.length; i++) {
                    this._textures[i].wrapV = wrap;
                }
            }
        },
        enumerable: false,
        configurable: true
    });
    MultiRenderTarget.prototype._initTypes = function (count, types, samplingModes, useSRGBBuffers, options) {
        for (var i = 0; i < count; i++) {
            if (options && options.types && options.types[i] !== undefined) {
                types.push(options.types[i]);
            }
            else {
                types.push(options && options.defaultType ? options.defaultType : 0);
            }
            if (options && options.samplingModes && options.samplingModes[i] !== undefined) {
                samplingModes.push(options.samplingModes[i]);
            }
            else {
                samplingModes.push(Texture.BILINEAR_SAMPLINGMODE);
            }
            if (options && options.useSRGBBuffers && options.useSRGBBuffers[i] !== undefined) {
                useSRGBBuffers.push(options.useSRGBBuffers[i]);
            }
            else {
                useSRGBBuffers.push(false);
            }
        }
    };
    /**
     * @param forceFullRebuild
     * @param textureNames
     * @hidden
     */
    MultiRenderTarget.prototype._rebuild = function (forceFullRebuild, textureNames) {
        if (forceFullRebuild === void 0) { forceFullRebuild = false; }
        if (this._count < 1) {
            return;
        }
        this.releaseInternalTextures();
        this._createInternalTextures();
        if (forceFullRebuild) {
            this._releaseTextures();
            this._createTextures(textureNames);
        }
        var internalTextures = this._renderTarget.textures;
        for (var i = 0; i < internalTextures.length; i++) {
            var texture = this._textures[i];
            texture._texture = internalTextures[i];
        }
        if (this.samples !== 1) {
            this._renderTarget.setSamples(this.samples, !this._drawOnlyOnFirstAttachmentByDefault, true);
        }
    };
    MultiRenderTarget.prototype._createInternalTextures = function () {
        this._renderTarget = this._getEngine().createMultipleRenderTarget(this._size, this._multiRenderTargetOptions, !this._drawOnlyOnFirstAttachmentByDefault);
        this._texture = this._renderTarget.texture;
    };
    MultiRenderTarget.prototype._releaseTextures = function () {
        if (this._textures) {
            for (var i = 0; i < this._textures.length; i++) {
                this._textures[i]._texture = null; // internal textures are released by a call to releaseInternalTextures()
                this._textures[i].dispose();
            }
        }
    };
    MultiRenderTarget.prototype._createTextures = function (textureNames) {
        var internalTextures = this._renderTarget.textures;
        this._textures = [];
        for (var i = 0; i < internalTextures.length; i++) {
            var texture = new Texture(null, this.getScene());
            if (textureNames === null || textureNames === void 0 ? void 0 : textureNames[i]) {
                texture.name = textureNames[i];
            }
            texture._texture = internalTextures[i];
            this._textures.push(texture);
        }
    };
    /**
     * Replaces an internal texture within the MRT. Useful to share textures between MultiRenderTarget.
     * @param texture The new texture to set in the MRT
     * @param index The index of the texture to replace
     * @param disposePrevious Set to true if the previous internal texture should be disposed
     */
    MultiRenderTarget.prototype.setInternalTexture = function (texture, index, disposePrevious) {
        if (disposePrevious === void 0) { disposePrevious = true; }
        if (!this.renderTarget) {
            return;
        }
        if (index === 0) {
            this._texture = texture;
        }
        this.renderTarget.setTexture(texture, index, disposePrevious);
        if (!this.textures[index]) {
            this.textures[index] = new Texture(null, this.getScene());
        }
        this.textures[index]._texture = texture;
        this._count = this.renderTarget.textures ? this.renderTarget.textures.length : 0;
        if (this._multiRenderTargetOptions.types) {
            this._multiRenderTargetOptions.types[index] = texture.type;
        }
        if (this._multiRenderTargetOptions.samplingModes) {
            this._multiRenderTargetOptions.samplingModes[index] = texture.samplingMode;
        }
        if (this._multiRenderTargetOptions.useSRGBBuffers) {
            this._multiRenderTargetOptions.useSRGBBuffers[index] = texture._useSRGBBuffer;
        }
    };
    Object.defineProperty(MultiRenderTarget.prototype, "samples", {
        /**
         * Define the number of samples used if MSAA is enabled.
         */
        get: function () {
            return this._samples;
        },
        set: function (value) {
            if (this._renderTarget) {
                this._samples = this._renderTarget.setSamples(value);
            }
            else {
                // In case samples are set with 0 textures created, we must save the desired samples value
                this._samples = value;
            }
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Resize all the textures in the multi render target.
     * Be careful as it will recreate all the data in the new texture.
     * @param size Define the new size
     */
    MultiRenderTarget.prototype.resize = function (size) {
        this._size = size;
        this._rebuild();
    };
    /**
     * Changes the number of render targets in this MRT
     * Be careful as it will recreate all the data in the new texture.
     * @param count new texture count
     * @param options Specifies texture types and sampling modes for new textures
     * @param textureNames Specifies the names of the textures (optional)
     */
    MultiRenderTarget.prototype.updateCount = function (count, options, textureNames) {
        this._multiRenderTargetOptions.textureCount = count;
        this._count = count;
        var types = [];
        var samplingModes = [];
        var useSRGBBuffers = [];
        this._initTypes(count, types, samplingModes, useSRGBBuffers, options);
        this._multiRenderTargetOptions.types = types;
        this._multiRenderTargetOptions.samplingModes = samplingModes;
        this._multiRenderTargetOptions.useSRGBBuffers = useSRGBBuffers;
        this._rebuild(true, textureNames);
    };
    MultiRenderTarget.prototype._unbindFrameBuffer = function (engine, faceIndex) {
        var _this = this;
        if (this._renderTarget) {
            engine.unBindMultiColorAttachmentFramebuffer(this._renderTarget, this.isCube, function () {
                _this.onAfterRenderObservable.notifyObservers(faceIndex);
            });
        }
    };
    /**
     * Dispose the render targets and their associated resources
     * @param doNotDisposeInternalTextures
     */
    MultiRenderTarget.prototype.dispose = function (doNotDisposeInternalTextures) {
        if (doNotDisposeInternalTextures === void 0) { doNotDisposeInternalTextures = false; }
        this._releaseTextures();
        if (!doNotDisposeInternalTextures) {
            this.releaseInternalTextures();
        }
        else {
            // Prevent internal texture dispose in super.dispose
            this._texture = null;
        }
        _super.prototype.dispose.call(this);
    };
    /**
     * Release all the underlying texture used as draw buffers.
     */
    MultiRenderTarget.prototype.releaseInternalTextures = function () {
        var _a, _b;
        var internalTextures = (_a = this._renderTarget) === null || _a === void 0 ? void 0 : _a.textures;
        if (!internalTextures) {
            return;
        }
        for (var i = internalTextures.length - 1; i >= 0; i--) {
            this._textures[i]._texture = null;
        }
        (_b = this._renderTarget) === null || _b === void 0 ? void 0 : _b.dispose();
        this._renderTarget = null;
    };
    return MultiRenderTarget;
}(RenderTargetTexture));
export { MultiRenderTarget };
//# sourceMappingURL=multiRenderTarget.js.map